import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useMember } from "@/context/MemberContext";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", info: null });
  const { refresh } = useMember();

  useEffect(() => {
    if (!sessionId) return setState({ phase: "error", info: null });
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { if (data.kind === "membership") refresh(); return setState({ phase: "paid", info: data }); }
        if (["failed", "expired", "refunded"].includes(data.payment_status)) return setState({ phase: "error", info: data });
      } catch {
        if (attempts >= MAX_POLLS) return setState({ phase: "error", info: null });
      }
      if (attempts >= MAX_POLLS) return setState({ phase: "timeout", info: null });
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, refresh]);

  const isMember = state.info?.kind === "membership";

  return (
    <div data-testid="payment-success-page" className="min-h-screen bg-ink text-gray-100 flex items-center justify-center px-6">
      <div className="max-w-lg w-full bg-elevate border border-white/10 p-10 clip-frame text-center">
        {state.phase === "checking" && (
          <>
            <Loader2 size={40} className="mx-auto animate-spin text-blood" />
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.3em] text-gray-400">Confirming your payment…</p>
          </>
        )}
        {state.phase === "paid" && (
          <>
            <CheckCircle2 size={48} className="mx-auto text-green-400" />
            <h1 data-testid="payment-success-title" className="mt-6 font-display font-black uppercase text-3xl sm:text-4xl">
              {isMember ? "Welcome, Insider" : "Respect. Thank you."}
            </h1>
            <p className="mt-4 text-gray-400 leading-relaxed">
              {isMember
                ? "Your $1.99/month Zo Pound Insider membership is active and you're signed in on this device. Full access to every episode, drop and the live chat."
                : `Your $${Number(state.info.amount).toFixed(2)} donation keeps THE ZO POUND EXPERIENCE on air. A receipt is on its way.`}
            </p>
          </>
        )}
        {state.phase === "timeout" && (
          <>
            <Loader2 size={40} className="mx-auto text-gray-400" />
            <h1 className="mt-6 font-display font-black uppercase text-2xl">Still processing</h1>
            <p className="mt-4 text-gray-400">Your payment is being confirmed. Check your email for the receipt.</p>
          </>
        )}
        {state.phase === "error" && (
          <>
            <XCircle size={48} className="mx-auto text-blood" />
            <h1 className="mt-6 font-display font-black uppercase text-2xl">Payment not completed</h1>
            <p className="mt-4 text-gray-400">No charge was made. You can try again anytime.</p>
          </>
        )}
        <Link to="/" data-testid="payment-back-home" className="mt-10 inline-block bg-blood hover:bg-red-700 text-white font-display font-bold uppercase tracking-wider px-8 py-3 transition-colors">
          Back to the show
        </Link>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div data-testid="payment-cancel-page" className="min-h-screen bg-ink text-gray-100 flex items-center justify-center px-6">
      <div className="max-w-lg w-full bg-elevate border border-white/10 p-10 clip-frame text-center">
        <XCircle size={48} className="mx-auto text-gray-400" />
        <h1 className="mt-6 font-display font-black uppercase text-3xl">Checkout cancelled</h1>
        <p className="mt-4 text-gray-400">No charge was made. The movement will be here when you're ready.</p>
        <Link to="/" data-testid="payment-cancel-back-home" className="mt-10 inline-block border border-white/25 hover:border-blood hover:text-blood font-display font-bold uppercase tracking-wider px-8 py-3 transition-colors">
          Back to the show
        </Link>
      </div>
    </div>
  );
}
